import { useEffect, useRef, useState } from 'react';
import { logPracticeControl, createDebouncedLogger } from './telemetry';
import { allVisibleUpdates, myPartOnlyUpdates, visibilityMode } from './partVisibility';
import { watchStepHints } from './stepHints';
import { lyricVersesByPart, chooseLyricPart } from './lyrics';

/**
 * Behaviour shared by the practice shells (phone and desktop): playback
 * state rules, the blend, the Show toggle, zoom, the "which part is me"
 * dialog, the section/loop rules, the metronome beat pulse, page locking
 * and telemetry. The shells themselves are layout only.
 *
 * Rules (agreed 2026-09-12): Stopped / Playing / Paused come from the audio
 * engine; a section is edited only while stopped and always loops; while a
 * section is looping, score taps are ignored; structural settings are
 * locked while not stopped.
 */

export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 3.0;
export const ZOOM_STEP = 0.1;
const BLEND_LOG_MS = 1000;
const ZOOM_LOG_MS = 500;
// After a load, a preset may still restore the saved "me" part; hold the
// "Pick my part" affordances back for this long so they don't flash.
const ME_PART_SETTLE_MS = 1500;
const BODY_CLASS = 'mobile-practice-active';
// Per-device preferences: half/whole step hints between the notes of my
// part, and the current word large over the score
const STEP_HINTS_KEY = 'musiker-step-hints';
const LYRICS_KEY = 'musiker-lyrics';
const LYRICS_MODE_KEY = 'musiker-lyrics-mode'; // 'word', 'phrase' or 'magnify'
const LYRICS_MODES = ['word', 'phrase', 'magnify'];

const readOnOffPref = (key) => {
  try { return localStorage.getItem(key) === 'on'; } catch (e) { return false; }
};
const writeOnOffPref = (key, on) => {
  try { localStorage.setItem(key, on ? 'on' : 'off'); } catch (e) { /* ignore */ }
};

export const rangeKey = (r) => (r && r.start !== null ? `${r.start}-${r.end === null ? r.start : r.end}` : '');

// Lock page scroll (iOS needs position:fixed on body to stop rubber-banding)
// and hide the app header while a shell is up; restore on the way out.
export function useBodyLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const scrollY = window.scrollY;
    document.body.classList.add(BODY_CLASS);
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.classList.remove(BODY_CLASS);
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

export function usePracticeShell({ active, controls, ui = 'mobile_shell' }) {
  const {
    user, fileName, getOsmd, getRenderCoordinator, isLoading,
    isPlaying, isPaused, canPlay, canPause, canStop, onPlay, onPause, onStop,
    tempoBPM, onTempoAdjust, onTempoSet, metronomeEnabled, setMetronomeEnabled,
    partControls, selectedPart, openMePartDialog,
    showMePartDialog, setShowMePartDialog, onMePartSelect,
    confidence, setConfidence, audioHook,
    zoom, setZoom, setVisibilityBatch, selectedRange, setScoreTapsDisabled,
    isLoopMode, onEnterLoopMode, onExitLoopMode, onClearSelection
  } = controls;

  const inPractice = isPlaying || isPaused;

  useBodyLock(active);

  // Visual beat while the metronome is on: 'beat' or 'downbeat' for ~110 ms
  // after each click, in sync with the audio clock.
  const [beat, setBeat] = useState('');
  const beatTimerRef = useRef(null);
  const { addBeatListener } = audioHook;
  useEffect(() => {
    if (!metronomeEnabled || !addBeatListener) return undefined;
    const unsubscribe = addBeatListener((isDownbeat) => {
      setBeat(isDownbeat ? 'downbeat' : 'beat');
      clearTimeout(beatTimerRef.current);
      beatTimerRef.current = setTimeout(() => setBeat(''), 110);
    });
    return () => {
      unsubscribe();
      clearTimeout(beatTimerRef.current);
      setBeat('');
    };
  }, [metronomeEnabled, addBeatListener]);

  // If a sheet was somehow laid out while the shell was hidden, re-render it
  // at the shell's real width once we become visible. Normally a no-op: the
  // shell activates on isLoading, before the first render.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (active && !wasActiveRef.current) {
      if (getOsmd?.()?.Sheet) {
        getRenderCoordinator?.()?.requestRender({ priority: 'immediate', reason: 'shell-activate' });
      }
      logPracticeControl({ user, fileName, ui, control: 'shell_open', metadata: { zoom, selectedPart } });
    }
    wasActiveRef.current = active;
  }, [active, getOsmd, getRenderCoordinator, user, fileName, ui, zoom, selectedPart]);

  const logBlend = useRef(createDebouncedLogger(BLEND_LOG_MS)).current;
  const logZoom = useRef(createDebouncedLogger(ZOOM_LOG_MS)).current;
  const logArgs = (control) => ({ user, fileName, ui, control });

  // ----- "which part is me" dialog -----
  // The player auto-prompts 800 ms after a load. A preset can restore the
  // saved part a moment later, and a reload can briefly empty partControls,
  // so only show the auto-prompt while there is genuinely something to pick.
  // Opening it from the badge (manual) always shows it.
  const hasMe = selectedPart !== null && selectedPart !== undefined;
  const manualMeDialogRef = useRef(false);
  const openMePart = () => {
    manualMeDialogRef.current = true;
    openMePartDialog();
  };
  const closeMePart = () => {
    manualMeDialogRef.current = false;
    setShowMePartDialog(false);
  };
  const selectMePart = (partId) => {
    manualMeDialogRef.current = false;
    onMePartSelect(partId);
  };
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!active || isLoading) {
      setSettled(false);
      return undefined;
    }
    const timer = setTimeout(() => setSettled(true), ME_PART_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [active, isLoading, fileName]);
  const mePending = !hasMe && !settled;
  const meDialogVisible = showMePartDialog && partControls.length > 1 && (manualMeDialogRef.current || (!hasMe && settled));
  useEffect(() => {
    if (showMePartDialog && !meDialogVisible) setShowMePartDialog(false);
  }, [showMePartDialog, meDialogVisible, setShowMePartDialog]);
  const mePartName = hasMe ? partControls.find(p => p.id === selectedPart)?.name : null;

  // ----- More panel and song picker -----
  const [moreOpen, setMoreOpen] = useState(false);
  const [songsOpen, setSongsOpen] = useState(false);
  useEffect(() => { if (!active) { setMoreOpen(false); setSongsOpen(false); } }, [active]);
  const openMore = () => {
    setMoreOpen(true);
    logPracticeControl(logArgs('more_open'));
  };

  // ----- parts override vs. the blend -----
  // Two ways to shape the mix conflict: the blend slider plus the Show
  // toggle (the default), or the per-part hear/show/volume controls. A
  // "parts override" switch makes them exclusive: while it is on, the
  // per-part controls take effect and the blend/Show are dimmed; while it
  // is off, the reverse. It resets when the song changes.
  const [partsOverride, setPartsOverride] = useState(false);
  useEffect(() => { setPartsOverride(false); }, [fileName]);

  // The blend only reaches the synths while the engine's override volumes
  // are on. Keep that in step with the switch: on when the blend should
  // drive the mix (a "me" part exists and parts override is off), off when
  // the per-part controls should.
  const { overrideVolumesEnabled, setOverrideVolumesEnabled, applyConfidenceToOverrides } = audioHook;
  useEffect(() => {
    if (!active) return;
    const wantBlend = hasMe && !partsOverride;
    if (wantBlend && !overrideVolumesEnabled) setOverrideVolumesEnabled(true);
    if (!wantBlend && overrideVolumesEnabled && partsOverride) setOverrideVolumesEnabled(false);
  }, [active, hasMe, partsOverride, overrideVolumesEnabled, setOverrideVolumesEnabled]);

  const handleBlendChange = (value) => {
    logBlend(logArgs('blend'), confidence, value);
    setConfidence(value);
    if (overrideVolumesEnabled && hasMe && partControls.length > 0) {
      applyConfidenceToOverrides(partControls.map(p => p.id), selectedPart, value);
    }
  };

  // ----- show toggle -----
  // Visibility batches schedule a 1.5 s countdown render; a tap should land
  // immediately, so follow up with an immediate request (executeRender
  // clears the countdown itself).
  const applyVisibility = (updates) => {
    if (updates.length === 0) return;
    setVisibilityBatch(updates);
    getRenderCoordinator?.()?.requestRender({ priority: 'immediate', reason: 'show-toggle' });
  };
  const showMine = () => {
    if (!hasMe) {
      openMePart();
      return;
    }
    logPracticeControl({ ...logArgs('show_parts'), metadata: { mode: 'mine' } });
    applyVisibility(myPartOnlyUpdates(partControls, selectedPart));
  };
  const showAll = () => {
    logPracticeControl({ ...logArgs('show_parts'), metadata: { mode: 'all' } });
    applyVisibility(allVisibleUpdates(partControls));
  };

  // ----- step hints -----
  // A "1/2" or "1" pill between neighbouring notes of my part wherever it
  // moves by a second. Drawn into OSMD's SVG after each render; the watcher
  // redraws whenever OSMD wipes the sheet. On a shared staff, follow the
  // divisi voice the singer has kept: lower only means the bottom note.
  const [stepHints, setStepHintsState] = useState(() => readOnOffPref(STEP_HINTS_KEY));
  const setStepHints = (on) => {
    setStepHintsState(on);
    writeOnOffPref(STEP_HINTS_KEY, on);
    logPracticeControl({ ...logArgs('step_hints'), metadata: { on } });
  };
  const mePart = hasMe ? partControls.find(p => p.id === selectedPart) : null;
  const stepVoice = mePart && mePart.upperVoice === false && mePart.lowerVoice !== false ? 'bottom' : 'top';
  const stepPartId = active && stepHints && hasMe && !isLoading ? selectedPart : null;
  useEffect(() => {
    const osmd = getOsmd?.();
    if (!osmd || stepPartId === null) return undefined;
    return watchStepHints(osmd, { instrumentId: stepPartId, voice: stepVoice });
  }, [stepPartId, stepVoice, getOsmd, fileName]);

  // ----- lyrics -----
  // The word (or phrase) being sung, large over the score while playing
  // (LyricsOverlay): my part's words, else the first part that has some.
  // The verse follows the repeats unless one is picked; the pick resets
  // with the song.
  const [lyricsOn, setLyricsOnState] = useState(() => readOnOffPref(LYRICS_KEY));
  const setLyricsOn = (on) => {
    setLyricsOnState(on);
    writeOnOffPref(LYRICS_KEY, on);
    logPracticeControl({ ...logArgs('lyrics'), metadata: { on } });
  };
  const [lyricsMode, setLyricsModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(LYRICS_MODE_KEY);
      return LYRICS_MODES.includes(saved) ? saved : 'word';
    } catch (e) { return 'word'; }
  });
  const setLyricsMode = (mode) => {
    setLyricsModeState(mode);
    try { localStorage.setItem(LYRICS_MODE_KEY, mode); } catch (e) { /* ignore */ }
    logPracticeControl({ ...logArgs('lyrics_mode'), metadata: { mode } });
  };
  const [lyricsVerse, setLyricsVerseState] = useState('auto');
  useEffect(() => { setLyricsVerseState('auto'); }, [fileName]);
  const setLyricsVerse = (verse) => {
    setLyricsVerseState(verse);
    logPracticeControl({ ...logArgs('lyrics_verse'), metadata: { verse } });
  };
  // Re-read which parts have words whenever a new sheet has loaded
  const [lyricVerses, setLyricVerses] = useState(() => new Map());
  const lyricSheetRef = useRef(null);
  useEffect(() => {
    if (!active || isLoading) return;
    const sheet = getOsmd?.()?.Sheet || null;
    if (sheet === lyricSheetRef.current) return;
    lyricSheetRef.current = sheet;
    setLyricVerses(lyricVersesByPart(sheet));
  }, [active, isLoading, fileName, getOsmd]);
  const lyricPartId = chooseLyricPart(lyricVerses, selectedPart, partControls);
  const lyricsFollowMe = lyricPartId !== null && hasMe && lyricPartId === String(selectedPart);
  // The magnifier draws my part, else the part whose words are shown
  const magnifyPartId = hasMe ? String(selectedPart) : lyricPartId;
  // The dock's one-tap toggle: Lyrics on in Magnify mode, or Lyrics
  // off (the other lyric views stay in Display > Lyrics)
  const magnifyOn = lyricsOn && lyricsMode === 'magnify';
  const toggleMagnify = () => {
    const on = !magnifyOn;
    if (on) {
      setLyricsModeState('magnify');
      try { localStorage.setItem(LYRICS_MODE_KEY, 'magnify'); } catch (e) { /* ignore */ }
    }
    setLyricsOnState(on);
    writeOnOffPref(LYRICS_KEY, on);
    logPracticeControl({ ...logArgs('magnify'), metadata: { on } });
  };

  // ----- zoom -----
  const stepZoom = (delta) => {
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((zoom + delta) * 10) / 10));
    if (next === zoom) return;
    logZoom(logArgs('zoom'), zoom, next);
    setZoom(next);
  };

  // ----- section / loop rules -----
  // A selected section always loops. The selection can only change while
  // stopped, so loop mode is armed right then and left when cleared.
  const loopRef = useRef({});
  loopRef.current = { inPractice, isLoopMode, onEnterLoopMode, onExitLoopMode };
  const selectionKey = rangeKey(selectedRange);
  useEffect(() => {
    if (!active) return;
    const { inPractice: busy, isLoopMode: looping, onEnterLoopMode: enter, onExitLoopMode: exit } = loopRef.current;
    if (selectionKey) {
      if (!busy) enter();
    } else if (looping) {
      exit();
    }
  }, [selectionKey, active]);

  // Tap rules. Stopped: a tap selects a bar. Playing or paused without a
  // section: a tap moves the playhead (the player owns that switch). Playing
  // or paused with a section looping: taps are ignored, because a jump would
  // reschedule the whole song and silently break the loop.
  const hasSection = selectionKey !== '';
  useEffect(() => {
    setScoreTapsDisabled(inPractice && hasSection);
  }, [inPractice, hasSection, fileName, setScoreTapsDisabled]);

  return {
    inPractice,
    beat,
    hasMe,
    mePending,
    mePartName,
    openMePart,
    closeMePart,
    selectMePart,
    meDialogVisible,
    moreOpen,
    setMoreOpen,
    openMore,
    songsOpen,
    setSongsOpen,
    hasSection,
    partsOverride,
    setPartsOverride,
    stepHints,
    setStepHints,
    lyrics: {
      on: lyricsOn,
      setOn: setLyricsOn,
      mode: lyricsMode,
      setMode: setLyricsMode,
      available: lyricPartId !== null,
      partId: lyricPartId,
      partName: lyricPartId !== null ? partControls.find(p => String(p.id) === lyricPartId)?.name : null,
      followsMe: lyricsFollowMe,
      mineHasNone: hasMe && lyricPartId !== null && !lyricsFollowMe,
      verses: lyricPartId !== null ? lyricVerses.get(lyricPartId) : [],
      verse: lyricsVerse,
      setVerse: setLyricsVerse,
      voice: lyricsFollowMe ? stepVoice : 'top',
      magnifyPartId,
      magnifyVerses: (magnifyPartId !== null && lyricVerses.get(magnifyPartId)) || []
    },
    magnify: { on: magnifyOn, available: lyricPartId !== null, onToggle: toggleMagnify },
    blend: { value: confidence, onChange: handleBlendChange, disabled: !hasMe, onPickPart: openMePart, dimmed: partsOverride },
    transport: { isPlaying, canPlay, canPause, canStop, onPlay, onPause, onStop },
    show: { mode: visibilityMode(partControls, selectedPart), onMine: showMine, onAll: showAll, dimmed: partsOverride },
    zoomControl: {
      value: zoom,
      onOut: () => stepZoom(-ZOOM_STEP),
      onIn: () => stepZoom(ZOOM_STEP),
      canOut: zoom > ZOOM_MIN + 0.001,
      canIn: zoom < ZOOM_MAX - 0.001
    },
    section: {
      start: selectedRange ? selectedRange.start : null,
      end: selectedRange ? selectedRange.end : null,
      onClear: onClearSelection,
      locked: inPractice
    },
    tempo: {
      bpm: tempoBPM,
      onAdjust: onTempoAdjust,
      onSet: onTempoSet,
      metronome: metronomeEnabled,
      onToggleMetronome: setMetronomeEnabled,
      beat
    }
  };
}
